import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import {
	MAIN_PATH,
	LOGIN_PATH,
	NUM_CATALOG,
	PRODUCT_CATALOG,
	TECH_CARDS_PATH,
	TECH_CARD_EDIT,
	TECH_CARD_INFO,
	TECH_CARD_NEW,
	USERS,
	ISSUES_PATH,
	TECH_CARD_OPERATION_PATH,
	ASSEMBLY_PATH,
	TECH_CARD_ASSEMBLY_NEW,
	TECH_CARD_ASSEMBLY_EDIT,
	TECH_CARD_ASSEMBLY_INFO,
	PREFORM_PATH,
	TECH_CARD_PREFORM_EDIT,
	TECH_CARD_PREFORM_INFO,
	TECH_CARD_PREFORM_NEW,
	COUNTER_AGENT_PATH,
	ASSEMBLY_TECHMAP_OPERATION_PATH,
	ASSEMBLY_TECHMAP_OPERATION_PATH_UNFILLED,
	PREFORM_TECHMAP_OPERATION_PATH,
	PREFORM_TECHMAP_OPERATION_PATH_UNFILLED,
	RUBBER_PRODUCT_CATALOG,
	TECH_CARD_RUBBER_EDIT,
	TECH_CARD_RUBBER_INFO,
	TECH_CARD_RUBBER_NEW,
	RUBBER_TECHMAP_OPERATION_PATH,
} from '@/shared/core';
import { useAppSelector } from '@/shared/state';
import {
	AssemblyPage,
	Error,
	IssuesPage,
	Login,
	NumCatalog,
	ProductCatalog,
	TechCardOperationPage,
	TechCardPage,
	TechCardPageAssembly,
	TechCardsPage,
	TechCardPreformPage,
	Ui,
	Users,
} from '@/pages';
import { RoleGuard } from '@/app/router/role-guard';
import { REDIRECT_PARAM } from '@/shared';
import { PreformPage } from '@/pages/preform';
import { CounterAgentPage } from '@/pages/counter-agent';
import { SkippedOperationsAlert } from '@/entities/alerts/skipped-operations';
import { AssemblyTechmapOperationPage } from '@/pages/assembly-techmap-operation';
import { RubberCatalogPage } from '@/pages/rubber-catalog';
import { PreformTechmapOperationPage } from '@/pages/preform-techmap-operation';
import { TechCardRubberPage } from '@/pages/tech-card-page/tech-card-rubber-page';
import { RubberTechmapOperationPage } from '@/pages/rubber-techmap-operation';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export const Router = () => {
	const userProfile = useAppSelector((state) => state.auth.userProfile);
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const LoginPageRedirect = () => {
		const url = LOGIN_PATH + '?' + REDIRECT_PARAM + '=' + pathname;
		return <Navigate to={url} />;
	};
	const TechCardsPageRedirect = () => <Navigate to={TECH_CARDS_PATH} />;

	return (
		<>
			{!userProfile ? (
				<SentryRoutes>
					<Route element={<Login />} path={LOGIN_PATH} />
					<Route path="*" element={<LoginPageRedirect />} />
				</SentryRoutes>
			) : (
				<SentryRoutes>
					<Route element={<TechCardOperationPage />} path={TECH_CARD_OPERATION_PATH} />
					<Route element={<AssemblyTechmapOperationPage />} path={ASSEMBLY_TECHMAP_OPERATION_PATH} />
					<Route element={<PreformTechmapOperationPage />} path={PREFORM_TECHMAP_OPERATION_PATH} />
					<Route element={<RubberTechmapOperationPage />} path={RUBBER_TECHMAP_OPERATION_PATH} />
					<Route element={<TechCardsPage />} path={TECH_CARDS_PATH} />
					<Route element={<TechCardsPageRedirect />} path={MAIN_PATH} />
					<Route element={<SkippedOperationsAlert />} path={ASSEMBLY_TECHMAP_OPERATION_PATH_UNFILLED} />
					<Route element={<SkippedOperationsAlert />} path={PREFORM_TECHMAP_OPERATION_PATH_UNFILLED} />
					{/* Для решения проблемы печати необходимо перемониторвать страницу после создания МК
					Для пешения этой пролемы, так как для всех роутов выводится один и тот же компонент, указываем ему разые ключи */}
					<Route element={<TechCardPage key="tech card view" />} path={TECH_CARD_INFO} />
					<Route element={<TechCardPage key="tech card create" />} path={TECH_CARD_NEW} />
					<Route element={<TechCardPage key="tech card edit" />} path={TECH_CARD_EDIT} />
					<Route element={<TechCardRubberPage key="tech card view" />} path={TECH_CARD_RUBBER_INFO} />
					<Route element={<TechCardRubberPage key="tech card create" />} path={TECH_CARD_RUBBER_NEW} />
					<Route element={<TechCardRubberPage key="tech card edit" />} path={TECH_CARD_RUBBER_EDIT} />
					<Route
						element={<TechCardPageAssembly key="assembly tech card create" />}
						path={TECH_CARD_ASSEMBLY_NEW}
					/>
					<Route
						element={<TechCardPageAssembly key="assembly tech card view" />}
						path={TECH_CARD_ASSEMBLY_INFO}
					/>
					<Route
						element={<TechCardPageAssembly key="assembly tech card edit" />}
						path={TECH_CARD_ASSEMBLY_EDIT}
					/>
					<Route element={<TechCardPreformPage key="perform tech card new" />} path={TECH_CARD_PREFORM_NEW} />
					<Route
						element={<TechCardPreformPage key="perform tech card view" />}
						path={TECH_CARD_PREFORM_INFO}
					/>
					<Route
						element={<TechCardPreformPage key="preform tech card edit" />}
						path={TECH_CARD_PREFORM_EDIT}
					/>
					<Route element={<NumCatalog />} path={NUM_CATALOG} />
					<Route element={<AssemblyPage />} path={ASSEMBLY_PATH} />
					<Route element={<PreformPage />} path={PREFORM_PATH} />
					<Route element={<CounterAgentPage />} path={COUNTER_AGENT_PATH} />
					<Route
						element={
							<RoleGuard role={['admin', 'accountant']}>
								<Users />
							</RoleGuard>
						}
						path={USERS}
					/>
					<Route element={<ProductCatalog />} path={PRODUCT_CATALOG} />
					<Route element={<RubberCatalogPage />} path={RUBBER_PRODUCT_CATALOG} />
					<Route element={<IssuesPage />} path={ISSUES_PATH} />
					<Route
						path="*"
						element={
							<Error
								status={404}
								description={
									'Похоже, что этой страницы не существует. Пожалуйста, убедитесь, что адрес введен правильно.'
								}
								buttonText="На главную"
								onConfirm={() => navigate(TECH_CARDS_PATH)}
							/>
						}
					/>
					{process.env.NODE_ENV !== 'production' ? <Route path="/ui" element={<Ui />} /> : null}
				</SentryRoutes>
			)}
		</>
	);
};
